import { type TypedDocumentNode, gql } from '@apollo/client';
import type { FC, PropsWithChildren } from 'react';
import { FormattedNumber } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  GameWinOddsProjection_teamGameStatsInterface,
  GameWinOddsProjection_teamGameStatsInterface_FootballTeamGameStats_,
} from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0 var(--unit);

  background: linear-gradient(
    to right,
    var(--c-left-color) -100%,
    transparent,
    var(--c-right-color) 200%
  );
`;

const validStat = (
  stat?: GameWinOddsProjection_teamGameStatsInterface | null
): stat is NonNullable<GameWinOddsProjection_teamGameStatsInterface_FootballTeamGameStats_> =>
  isType(stat, 'FootballTeamGameStats') && !!stat;

type Props = {
  Wrapper?: FC<PropsWithChildren>;
  homeTeamGameStatsInterface?: GameWinOddsProjection_teamGameStatsInterface | null;
  awayTeamGameStatsInterface?: GameWinOddsProjection_teamGameStatsInterface | null;
};
export const GameWinOddsProjection = ({
  Wrapper,
  homeTeamGameStatsInterface,
  awayTeamGameStatsInterface,
}: Props) => {
  if (
    !(
      validStat(homeTeamGameStatsInterface) &&
      homeTeamGameStatsInterface.winOdds &&
      validStat(awayTeamGameStatsInterface) &&
      awayTeamGameStatsInterface.winOdds
    )
  ) {
    return null;
  }

  const percentageHomeTeamWinOdds = 1 / homeTeamGameStatsInterface.winOdds;
  const percentageAwayTeamWinOdds = 1 / awayTeamGameStatsInterface.winOdds;
  const percentageDrawOdds =
    1 - percentageHomeTeamWinOdds - percentageAwayTeamWinOdds;

  const leftColor =
    percentageHomeTeamWinOdds > percentageAwayTeamWinOdds
      ? 'var(--c-green-400)'
      : 'var(--c-score-low)';
  const rightColor =
    percentageAwayTeamWinOdds > percentageHomeTeamWinOdds
      ? 'var(--c-green-400)'
      : 'var(--c-score-low)';

  const content = (
    <Root
      style={
        {
          '--c-left-color': leftColor,
          '--c-right-color': rightColor,
        } as CSSProperties
      }
    >
      <LabelS bold color={leftColor}>
        <FormattedNumber value={percentageHomeTeamWinOdds} style="percent" />
      </LabelS>
      <LabelS bold color="var(--c-nd-600)">
        <FormattedNumber value={percentageDrawOdds} style="percent" />
      </LabelS>
      <LabelS bold color={rightColor}>
        <FormattedNumber value={percentageAwayTeamWinOdds} style="percent" />
      </LabelS>
    </Root>
  );

  if (Wrapper) {
    return <Wrapper>{content}</Wrapper>;
  }
  return content;
};

GameWinOddsProjection.fragments = {
  teamGameStatsInterface: gql`
    fragment GameWinOddsProjection_teamGameStatsInterface on TeamGameStatsInterface {
      id
      ... on FootballTeamGameStats {
        id
        winOdds
      }
    }
  ` as TypedDocumentNode<GameWinOddsProjection_teamGameStatsInterface>,
};
